<template>
  <div v-if="isLoading">
    <LoadingSpinner />
  </div>
  <div v-else>
    <NotificationsComponent context="BuyflowComponent" />
    <article
      v-if="buyFlowStore.layoutState === layoutStates.purchase"
      class="main pa-desktop constrain-90 m-auto px-half"
    >
      <BuyProgressbar />
      <Cart />
      <AccountCreditEarned :adjustments="buyFlowStore?.cart?.adjustments || []" />
      <VatInputComponent class="mb" />
      <PromoCode class="mb" @remove-promo-code="removePromoCode" />
      <SelectPlanLink v-if="buyFlowStore.hasCartItems && FLAGS.ENABLE_BUYFLOW_WEBROOT" class="mb" />
      <SpecialOffers v-if="buyFlowStore.hasCartItems && FLAGS.ENABLE_BUYFLOW_WEBROOT" class="mb" />
      <BuyEmail v-if="buyFlowStore.hasCartItems && FLAGS.ENABLE_BUYFLOW_WEBROOT" class="mb" />
      <BuyPayment class="mb" />
      <ReviewOrder class="mb" />
      <PhoneAssistance
        v-if="(FLAGS.ENABLE_BUYFLOW_WEBROOT && currentBrand === 'Webroot') || webrootProductWasInCart()"
        class="mb"
      />
    </article>
    <article
      v-else-if="buyFlowStore.layoutState === layoutStates.editShipping"
      class="main pa-desktop constrain-90 m-auto px-half"
    >
      <EditShipping />
    </article>
    <article
      v-else-if="buyFlowStore.layoutState === layoutStates.editPayment"
      class="main pa-desktop constrain-90 m-auto px-half"
    >
      <EditPayment />
    </article>
    <LoadingBoxComponent
      :show-dialog="buyFlowStore.showProcessingOrderMessage || false"
      :message="t('Common.loading')"
    />
  </div>
</template>

<script setup lang="ts">
//Components
import SelectPlanLink from "@/components/Buy/SelectPlanLink.vue";
import BuyProgressbar from "./BuyProgressbar.vue";
import VatInputComponent from "./ReviewOrder/VatInput.vue";
import Cart from "@/components/Buy/Cart/Cart.vue";
import BuyEmail from "./Email/Email.vue";
import BuyPayment from "./Payment/Payment.vue";
import PromoCode from "./ReviewOrder/PromoCode.vue";
import ReviewOrder from "./ReviewOrder/ReviewOrder.vue";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import NotificationsComponent from "@/components/shared/Notifications.vue";
import LoadingBoxComponent from "@/components/shared/LoadingBox.vue";
import EditShipping from "@/components/Buy/ReviewOrder/EditShipping.vue";
//Other
import { logEvent } from "@/common/logger";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useUnauthenticatedBuyFlowStore } from "@/stores/unauthorizedBuyflow";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { useUserStore } from "@/stores/user";
import { DEFAULT_SKU, DEFAULT_TERM_LENGTH_MONTHS, DEFAULT_QUANTITY, FLAGS } from "@/define";
import { pageStates, layoutStates } from "@/components/Buy/BuyEnums";
import { ISetupCartRequest, IPurchaseError } from "@/components/Buy/Interfaces";
import { getRedirectToLoginURL } from "@/common/loginRedirect";
import {
  buildQueryString,
  setupCartForUser,
  setupCartNewUser,
  checkLoggedStatus,
  getProspectId,
  redirectToOutstandingInvoice,
} from "@/components/Buy/BuyHelper";
import { useRoute, useRouter } from "vue-router";
import { changePageState } from "@/components/Buy/BuyHelper";
import AccountCreditEarned from "@/components/Buy/AccountCreditEarned.vue";
import { AxiosError } from "axios";
import { HTTP_STATUS_BAD_REQUEST, HTTP_STATUS_NOT_FOUND } from "@/define";
import { handleApiError } from "@/common/handleApiError";
import SpecialOffers from "@/components/Buy/SpecialOffers.vue";
import { sortArrayOfObjects } from "./commonFn";
import PhoneAssistance from "@/components/Buy/PhoneAssistance.vue";
import { validGuidFormat } from "@/globalRegex";
import { t } from "@/i18n";
import EditPayment from "@/components/Buy/Payment/EditPayment.vue";

const componentName = "BuyComponent";
logEvent("created", componentName);

const route = useRoute();
const router = useRouter();
const subscriptionStore = useSubscriptionsStore();
const unauthenticatedBuyflowStore = useUnauthenticatedBuyFlowStore();
const userStore = useUserStore();
const isLoading = ref<boolean>(false);
const buyFlowStore = useBuyFlowStore();
const currentBrand = computed(() => {
  return buyFlowStore.plans?.find(p => p.sku === buyFlowStore.cart?.items[0]?.sku)?.brand;
});

// const userIsLoggedIn = computed(() => {
//   return user.currentUser;
// });

// commented while not in use
// function tn(v: string, params?: Record<string, unknown>): string {
//   return t(`${componentName}.${v}`, params);
// }

//Check query parameters
let sku = route.query.sku?.toString() || "";
let ratePlanId =
  route.query?.ratePlanId?.toString() || route.query?.rateplanid?.toString() || route.query?.rpi?.toString() || "";
const prospectGuid = route.query?.guid?.toString() || route.query?.pguid?.toString() || "";
let step = route.query?.step?.toString() || pageStates.email.toString(); //Can't short hand do to navigation
let deviceId = parseInt(
  route.query?.deviceId?.toString() || route.query?.deviceid?.toString() || route.query?.did?.toString() || "0"
);
let userGuid =
  route.query?.userGuid?.toString() || route.query?.userguid?.toString() || route.query?.ug?.toString() || "";
let subscriptionMonths =
  parseInt(
    route.query?.subscriptionMonths?.toString() ||
      route.query?.subscriptionmonths?.toString() ||
      route.query?.sm?.toString() ||
      ""
  ) || DEFAULT_TERM_LENGTH_MONTHS;
let quantity = parseInt(route.query?.quantity?.toString() || route.query?.qty?.toString() || "1");
let userId = parseInt(route.query?.userId?.toString() || route.query?.uid?.toString() || "") || 0;
let promoCode =
  route.query.couponCode?.toString() || route.query.couponcode?.toString() || route.query.cc?.toString() || "";
const catId = parseInt(
  route.query.catId?.toString() || route.query.catid?.toString() || route.query.cid?.toString() || "0"
);
let subscriptionNumber =
  route.query.subscriptionNumber?.toString() ||
  route.query.subscriptionnumber?.toString() ||
  route.query.sn?.toString() ||
  "";
let includedUnits = parseInt(route.query.iu?.toString() || "1");
const webrootProductWasInCart = function () {
  if (buyFlowStore.isWebrootSku(buyFlowStore.lastCartItemRemoved?.sku || "")) {
    return true;
  }
};
const licenseKey = route.query.lk?.toString() || "";
const isWebrootFromQuery = route.query.iswr?.toString() === "1";
const loadCartFromDB = route.query.lcd?.toString() || 0;
buyFlowStore.referralUrl =
  route.query.referralUrl?.toString() || route.query.referralurl?.toString() || buyFlowStore.referralUrl || "";

const callerURL = document.referrer;
let isWebrootPurchase = isWebrootFromQuery || callerURL.indexOf("webroot") !== -1;

onBeforeMount(async () => {
  //Make sure the user guid is a valid format for a guid
  if (!validGuidFormat.test(userGuid)) {
    userGuid = "";
  }

  //Set DeviceId to 0 if we get passed a value that can not parse to an int
  if (isNaN(deviceId)) {
    deviceId = 0;
  }
  //Popstate is triggered every time the back OR forward browser button is used.
  window.onpopstate = function (event) {
    const params = event.state.current?.split("?")[1]?.split("&");
    const currentStep = params?.find(p => p.includes("step="))?.split("=")[1];
    let step = pageStates.email;
    if (currentStep) {
      step = currentStep;
    }

    if (step !== buyFlowStore.pageState) {
      changePageState(step);
    }
  };

  if (step) {
    if (!Object.values(pageStates).includes(step as pageStates)) {
      step = pageStates.email;
    }
    changePageState(pageStates[step]);
  }

  isLoading.value = true;
  await checkLoggedStatus();
  //If we have a userId we want to redirect them to log in

  // If userGuid and userId passed, userId is ignored
  if (userGuid && userId) {
    userId = 0;
  }

  if (userId > 0) {
    //create return URL without the userId
    let url = window.location.origin;
    const queryParams = buildQueryString(route.query, ["userId"]);

    if (queryParams) {
      url = `${url}/buy?${queryParams}`;
    }

    window.location.href = getRedirectToLoginURL(url);
  }

  let isRenewOrUpgrade = false;
  if (
    deviceId > 0 ||
    (buyFlowStore.cart && buyFlowStore.cart.items.some(l => l.computerId > 0)) ||
    buyFlowStore.isRenewOrUpgrade
  ) {
    isRenewOrUpgrade = true;
  }

  //Make sure the plans are populated
  await buyFlowStore.populatePlans(catId, isRenewOrUpgrade);
  isWebrootPurchase = isWebrootPurchase || buyFlowStore.isWebrootSku(sku);

  if (loadCartFromDB === "1") {
    try {
      await buyFlowStore.getShoppingCart(userGuid);
      buyFlowStore.userGuid = userGuid;
      buyFlowStore.pageState = pageStates.payment;
      //if there is a promo code add it to the cart
      if (promoCode && buyFlowStore.cart) {
        buyFlowStore.cart.promotionCode = promoCode;
        await buyFlowStore.updateShoppingCartTotal();
      }
      isLoading.value = false;
      return;
    } catch (err) {
      logEvent("Error on getting cart", JSON.stringify(err as AxiosError));
    }
  }

  // Check if sku and months on queryString are valid
  //This check is only for Carbonite products
  if (sku && subscriptionMonths && !isWebrootPurchase) {
    const ratePlan = buyFlowStore.getRatePlanIdBySku(sku, subscriptionMonths);
    if (!ratePlan) {
      sku = buyFlowStore.isSkuValid(sku) ? sku : "PersonalPlus";
      subscriptionMonths = 12;
    } else if (ratePlanId && ratePlanId != ratePlan) {
      // if ratePlanId passed in URL is different (invalid) then productCatalog, override.
      ratePlanId = ratePlan;
    }
  } else if (sku && subscriptionMonths && isWebrootPurchase) {
    //Set defaults for Webroot here
    //Default for WR sku will be an error message
    const skuRatePlans = buyFlowStore.getRatePlansForSku(sku);
    let seats = skuRatePlans.find(l => l.includedUnits === includedUnits && l.canPurchaseOnline)?.includedUnits;

    if (!seats && skuRatePlans.length > 0) {
      const sortedRatePlans = sortArrayOfObjects(skuRatePlans, "months", "ascending");
      seats = sortedRatePlans[0].includedUnits;

      if (seats) {
        includedUnits = seats;
      }
    }
  }

  // Check if quantity is greater or less than allowed
  // There are also some items that cannot be purchased more than 1 unit per customer
  quantity =
    quantity > buyFlowStore.getMaxQuantityBySku(sku)
      ? DEFAULT_QUANTITY
      : quantity < DEFAULT_QUANTITY || !quantity
      ? DEFAULT_QUANTITY
      : quantity;

  // if deviceId is passed, force quantity equals 1
  if (deviceId > 0) {
    quantity = 1;
  }

  if (!buyFlowStore.cart || buyFlowStore.cart?.items.length <= 0) {
    await setupCart();
  }

  // If there is nothing in the cart, we may wish to set further defaults than the PersonalPlus and annual set here
  if (!buyFlowStore.cart || buyFlowStore.cart?.items.length === 0) {
    const ratePlanId = buyFlowStore.getRatePlanIdBySku(DEFAULT_SKU, DEFAULT_TERM_LENGTH_MONTHS);
    const beginShoppingCartRequest = {
      sku: DEFAULT_SKU,
      ratePlanId: ratePlanId,
      quantity: quantity,
      deviceId: deviceId,
      subscriptionNumber: subscriptionNumber,
    };
    buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
    try {
      await buyFlowStore.updateShoppingCartTotal(false, true);
    } catch (error) {
      await handleErrorOnUpdate(error);
    }
    buyFlowStore.pageState = pageStates.email;
  }
  isLoading.value = false;
});

//This will look at all the data and setup the cart
async function setupCart() {
  let newUser = true;
  buyFlowStore.userGuid = userGuid;
  // We may use that in future
  //const referralUrl = route.query?.referralUrl?.toString() || "";

  const request = {
    ratePlanId: ratePlanId,
    sku: sku,
    step: step,
    deviceId: deviceId,
    userGuid: userGuid,
    subscriptionMonths: subscriptionMonths,
    quantity: quantity,
    subscriptionNumber: subscriptionNumber,
    includedUnits: includedUnits,
    licenseKey: licenseKey,
    isWebroot: isWebrootPurchase,
  } as ISetupCartRequest;

  //TODO: Figure out which state each flow should land on
  //We already have a logged in user or userGuid
  if (userStore.currentUser || userGuid || deviceId > 0) {
    newUser = false;
    if (userStore.currentUser) {
      //Populate the subscriptions store is we have a User
      await subscriptionStore.populateSubscriptions();
      if (deviceId > 0) {
        if (subscriptionStore.subscriptions.find(l => l.computer?.id === deviceId)?.hasOutstandingInvoices) {
          redirectToOutstandingInvoice();
          return;
        }
      }
    } else if (deviceId > 0) {
      //Get the subscription for the given device / userGuid
      try {
        await unauthenticatedBuyflowStore.populateSubscription(deviceId);
        if (unauthenticatedBuyflowStore?.subscription?.hasOutstandingInvoices) {
          redirectToOutstandingInvoice(true);
          return;
        }
      } catch (e) {
        const err = e as AxiosError;
        if (err.response?.status === HTTP_STATUS_BAD_REQUEST) {
          // If a UserGuid is invalid, we need to behave like a new user
          logEvent("Populate Subscription failed", componentName, e);
          userGuid = "";
          buyFlowStore.userGuid = "";
          request.userGuid = "";
          request.step = pageStates.email;
          newUser = true;
        } else {
          handleApiError(err, false);
        }
      }
    }

    if (!newUser) {
      await setupCartForUser(request);
      if (userGuid) {
        if (buyFlowStore.cart) {
          buyFlowStore.cart.userGuid = userGuid;
        }
      }
    }
  }

  if (newUser) {
    setupCartNewUser(request);
  }

  if (promoCode && buyFlowStore.cart) {
    buyFlowStore.cart.promotionCode = promoCode;
  }

  if (catId && buyFlowStore.cart) {
    buyFlowStore.cart.campaignId = catId;
  }

  //If prospectGuid exist in query params then fetch the prospectId from API and save to Buyflow store
  if (prospectGuid != "" && buyFlowStore.cart) {
    const prospectId = (await getProspectId(prospectGuid)) ?? 0;
    buyFlowStore.cart.prospectId = prospectId;
  }

  try {
    await buyFlowStore.updateShoppingCartTotal(false, true);
  } catch (e) {
    const err = e as AxiosError;
    if (err.response?.status === HTTP_STATUS_NOT_FOUND) {
      userGuid = "";
      buyFlowStore.userGuid = "";
      buyFlowStore.pageState = pageStates.email;
    } else {
      handleErrorOnUpdate(err);
    }
  }
}

//Watch the buyflow store pageState for changes
watch(
  () => buyFlowStore.$state.pageState,
  (stateChanged, previousState) => {
    //Prevent router push adding in extra web history by pushing the same state
    if (previousState && stateChanged === previousState) return;
    logEvent(`Buyflow state change to ${stateChanged}`, componentName);
    buyFlowStore.clearUndo();
    //If there is a logged in established user, skip the email step and do not put it on the history
    if (
      stateChanged === pageStates.email &&
      ((userStore.currentUser && userStore.currentUser?.subscriberInfo?.hasPasswordSet) || deviceId)
    ) {
      buyFlowStore.pageState = pageStates.payment;
    } else if (
      stateChanged !== pageStates.email &&
      !userStore.currentUser &&
      !userGuid &&
      deviceId === 0 &&
      !userStore.email
    ) {
      changePageState(pageStates.email);
    } else {
      //Every time the page state changes we need to push a new state to the route history
      //Make sure all query params are copied with the new state
      const queryParams = Object.assign({}, route.query, { step: stateChanged });
      router.push({ path: "buy", query: queryParams });
    }
  }
);

// For some preview errors we want to retry the preview call with a modified cart
// This method modifies the cart depending on the error
async function handleErrorOnUpdate(error) {
  let retryPreview = false;
  const axiosError = error as AxiosError;
  if (buyFlowStore.cart) {
    const response = axiosError.response?.data as IPurchaseError;

    if ((response.message === "InvalidComputerId" || response.message === "SmbComputerIneligible") && deviceId > 0) {
      // If deviceId query string is greater then 0 and is invalid
      // Override all computerIds in the items = 0
      deviceId = 0;
      for (const item of buyFlowStore.cart.items) {
        item.computerId = 0;
      }
      retryPreview = true;
    } else if (response.message === "InvalidPromotionCode" && promoCode) {
      // If promoCode query string is passed and is invalid
      // Override promotionCode to empty
      promoCode = "";
      buyFlowStore.cart.promotionCode = "";
      retryPreview = true;
    } else if (response.message === "InvalidSubscription" && subscriptionNumber) {
      // If subscriptionNumber is passed then 0 and is invalid
      // Override subscriptionNumbers to empty
      subscriptionNumber = "";
      for (const item of buyFlowStore.cart.items) {
        item.subscriptionNumbers = [];
      }
      retryPreview = true;
    }
  }
  if (retryPreview) {
    await buyFlowStore.updateShoppingCartTotal(false, true);
  } else {
    handleApiError(axiosError, false);
  }
}

async function removePromoCode() {
  if (buyFlowStore.cart) {
    buyFlowStore.cart.promotionCode = "";
  }
  promoCode = "";
  await buyFlowStore.updateShoppingCartTotal();
}
</script>
